import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import API from '../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { CheckCircle2, Clock, Plus, Trash2 } from 'lucide-react';

/**
 * Employee-facing: upload today's timesheet as a list of task entries.
 * Each task has a description + hours. Total is auto-computed.
 * Pre-fills existing entry for the day if one exists (edit flow).
 */
const blankTask = () => ({ description: '', hours: '' });

export default function TimesheetUploadDialog({ board, onClose, onSaved }) {
  const [tasks, setTasks] = useState([blankTask()]);
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState(null);
  const today = new Date().toISOString().split('T')[0];

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/daily-timesheet/mine`, {
        params: { board, date: today },
        withCredentials: true,
      });
      if (data && data.entry_id) {
        setExisting(data);
        if (Array.isArray(data.tasks) && data.tasks.length > 0) {
          setTasks(data.tasks.map(t => ({
            description: t.description || '',
            hours: String(t.hours ?? ''),
          })));
        } else if (data.description) {
          // Legacy single-entry fallback
          setTasks([{ description: data.description, hours: String(data.hours ?? '') }]);
        }
      }
    } catch { /* ignore — fresh upload */ }
  }, [board, today]);

  useEffect(() => { load(); }, [load]);

  const totalHours = tasks.reduce((s, t) => {
    const h = parseFloat(t.hours);
    return s + (isNaN(h) ? 0 : h);
  }, 0);

  const updateTask = (i, field, value) => {
    setTasks(prev => prev.map((t, idx) => idx === i ? { ...t, [field]: value } : t));
  };

  const addTask = () => setTasks(prev => [...prev, blankTask()]);
  const removeTask = (i) => setTasks(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

  const handleSave = async () => {
    // Validate
    const cleaned = [];
    for (const [i, t] of tasks.entries()) {
      const desc = (t.description || '').trim();
      const h = parseFloat(t.hours);
      if (!desc) { toast.error(`Task ${i + 1}: description required`); return; }
      if (isNaN(h) || h < 0 || h > 24) { toast.error(`Task ${i + 1}: enter hours 0-24`); return; }
      cleaned.push({ description: desc, hours: h });
    }
    if (totalHours > 24) { toast.error(`Total hours (${totalHours.toFixed(2)}) cannot exceed 24`); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/daily-timesheet`, { board, tasks: cleaned }, { withCredentials: true });
      toast.success(existing ? 'Timesheet updated' : 'Timesheet uploaded');
      onSaved?.();
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(e => e.msg).join(', ') : d || 'Failed to save');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-xl bg-[#0a0a0f] border-white/10 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-heading text-lg flex items-center gap-2">
            <Clock size={16} className="text-emerald-400" />
            {existing ? "Update Today's Timesheet" : "Upload Today's Timesheet"}
          </DialogTitle>
        </DialogHeader>

        {existing && (
          <div className="flex items-center gap-2 px-3 py-2 rounded bg-emerald-500/10 border border-emerald-500/20 text-[11px] text-emerald-300">
            <CheckCircle2 size={12} />
            You already submitted for today. Editing will update the entry.
          </div>
        )}

        <div className="space-y-3 py-2">
          <div className="flex justify-between text-xs">
            <span className="text-slate-500">Date</span>
            <span className="text-slate-300 font-mono">{today}</span>
          </div>

          <div className="space-y-2">
            {tasks.map((task, i) => (
              <div
                key={i}
                className="bg-white/[0.02] border border-white/5 rounded-lg p-3"
                data-testid={`timesheet-task-row-${i}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Task {i + 1}</span>
                  {tasks.length > 1 && (
                    <button
                      onClick={() => removeTask(i)}
                      className="text-slate-500 hover:text-rose-400 p-0.5"
                      data-testid={`timesheet-task-remove-${i}`}
                      type="button"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-[1fr_100px] gap-2">
                  <textarea
                    value={task.description}
                    onChange={(e) => updateTask(i, 'description', e.target.value)}
                    placeholder="What did you work on?"
                    rows={2}
                    className="w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
                    data-testid={`timesheet-task-desc-${i}`}
                  />
                  <div className="flex flex-col">
                    <input
                      type="number"
                      min="0"
                      max="24"
                      step="0.25"
                      value={task.hours}
                      onChange={(e) => updateTask(i, 'hours', e.target.value)}
                      placeholder="Hrs"
                      className="w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 text-right font-mono"
                      data-testid={`timesheet-task-hours-${i}`}
                    />
                    <span className="text-[9px] text-slate-600 text-right mt-1">hours</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addTask}
            className="w-full flex items-center justify-center gap-1 py-2 rounded-lg border border-dashed border-white/10 hover:border-indigo-500/40 hover:bg-indigo-500/5 text-xs text-slate-400 hover:text-indigo-300 transition-colors"
            data-testid="timesheet-add-task"
          >
            <Plus size={12} /> Add another task
          </button>

          <div className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20">
            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-300">Total Time Today</span>
            <span className={`text-lg font-heading font-black font-mono ${totalHours > 24 ? 'text-rose-400' : 'text-emerald-300'}`} data-testid="timesheet-total-hours">
              {totalHours.toFixed(2)}h
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={loading || tasks.length === 0 || totalHours > 24}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="timesheet-submit"
          >
            {loading ? 'Saving…' : (existing ? 'Update' : 'Upload')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
